import React, { useEffect, useRef, useState } from 'react'
import {
  Breadcrumb,
  Checkbox,
  notification,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/core/store'
import { Button, PageHeader } from 'src/components/Ant'
import { currencyMask } from 'src/components/Formik'
import { Id, PolicyAntiFraud } from 'src/types'
import * as PoliciesService from 'src/services/api/policies'
import useDocumentTitle from 'src/hooks/use-document-title'

import {
  getDefaultValues,
  initialValues
} from './anti-fraud-drawer/initial-values'
import * as CSS from './styles'
import AntiFraudDrawer from './anti-fraud-drawer'
import sanitizeToPost from './sanitize'

export default function AntiFraud() {
  useDocumentTitle('web.anti-fraud.title')
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef(null)
  const isSubscribed = useRef(true)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [dataSource, setDataSource] = useState<
    (PolicyAntiFraud & { key: Id })[]
  >([])
  const [hasError, setHasError] = useState<string>('')

  const { t } = useTranslation()

  useEffect(
    () => () => {
      isSubscribed.current = false
    },
    []
  )

  useEffect(() => {
    setIsLoading(true)
    setHasError('')
    PoliciesService.getAntiFraudList({
      organization_id: me.user.organization_id
    })
      .then(response => {
        if (isSubscribed.current) {
          if (response.status === 200) {
            setDataSource(
              response.data?.policies?.map(pol => ({
                ...pol,
                key: pol.policy_request_receipt_antifraud_id
              })) || []
            )
            return
          }
          setHasError('common.response.unexpected-error')
        }
      })
      .catch(response => {
        if (isSubscribed.current) {
          setHasError('common.response.unexpected-error')
        }
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.user.organization_id])

  const header = (
    <Breadcrumb>
      <Breadcrumb.Item>{t('web.administration.title')}</Breadcrumb.Item>
      <Breadcrumb.Item>{t('web.resources.title-plural')}</Breadcrumb.Item>
      <Breadcrumb.Item>{t('web.anti-fraud.title')}</Breadcrumb.Item>
    </Breadcrumb>
  )

  if (hasError) {
    return (
      <>
        <CSS.Breadcrumbs>{header}</CSS.Breadcrumbs>
        <PageHeader title="web.anti-fraud.title" />
        <div>{hasError}</div>
      </>
    )
  }

  const title = 'web.anti-fraud.title'
  const columns: ColumnProps<PolicyAntiFraud>[] = [
    {
      dataIndex: 'organization',
      render: (organization: { trade_name: string }) =>
        organization && <Tag color="green">{organization?.trade_name}</Tag>,
      title: (
        <Typography.Text strong>{t('web.organization.title')}</Typography.Text>
      )
    },
    {
      dataIndex: 'description',
      render: (description: string = '') => {
        if (description?.length > 50) {
          return (
            <Tooltip title={description}>
              <Typography.Text>
                {`${description.substring(0, 47)}...`}
              </Typography.Text>
            </Tooltip>
          )
        }
        return <Typography.Text>{description}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.anti-fraud.description')}
        </Typography.Text>
      )
    },
    {
      dataIndex: 'policy_rule',
      render: (policy_rule: { name: string }) => {
        if (policy_rule?.name?.length > 50) {
          return (
            <Typography.Text>
              {`${policy_rule.name.substring(0, 47)}...`}
            </Typography.Text>
          )
        }
        return <Typography.Text>{policy_rule?.name}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.anti-fraud.policy-rule')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'vat',
      render: (vat: boolean) => <Checkbox disabled checked={vat} />,
      title: <Typography.Text strong>{t('web.anti-fraud.vat')}</Typography.Text>
    },
    {
      align: 'center',
      dataIndex: 'value',
      render: (value: number) => {
        if (!value) {
          return <Typography.Text>{t('common.undefined')}</Typography.Text>
        }
        return (
          <Typography.Text>
            {
              currencyMask({
                formatStr: true,
                value
              }).maskedValue
            }
          </Typography.Text>
        )
      },
      title: (
        <Typography.Text strong>
          {t('web.anti-fraud.maximum-value-allowed')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: 'quantity',
      render: (quantity: number = 0) => {
        if (!quantity) {
          return <Typography.Text>{t('common.undefined')}</Typography.Text>
        }
        return <Typography.Text>{quantity}</Typography.Text>
      },
      title: (
        <Typography.Text strong>
          {t('web.anti-fraud.maximum-quantity-allowed')}
        </Typography.Text>
      )
    },
    {
      align: 'center',
      dataIndex: '',
      render: (record: PolicyAntiFraud) => (
        <CSS.Switch
          checked={record.active}
          checkedChildren={t('common.active')}
          data-test-id="antifraud-active"
          onChange={() => {
            setIsLoading(true)
            PoliciesService.saveAntiFraud({
              organization_id: me.user.organization_id,
              record: { ...record, active: !record.active }
            })
              .then(response => {
                if (isSubscribed.current) {
                  if (response.status === 200) {
                    const newRecord: PolicyAntiFraud & { key: Id } = {
                      ...response.data.policy_request_receipt_antifrauds[0],
                      key: response.data.policy_request_receipt_antifrauds[0]
                        .policy_request_receipt_antifraud_id
                    }
                    setDataSource(data =>
                      data.map(d =>
                        d.policy_request_receipt_antifraud_id ===
                        newRecord.policy_request_receipt_antifraud_id
                          ? newRecord
                          : d
                      )
                    )
                    notification.success({
                      duration: 10,
                      message: t('common.edit-done-successfully'),
                      placement: 'bottomRight'
                    })
                    return
                  }
                  notification.warning({
                    duration: 10,
                    message: t('common.response.unexpected-error'),
                    placement: 'bottomRight'
                  })
                }
              })
              .catch(response => {
                notification.warning({
                  duration: 10,
                  message: t('common.response.unexpected-error'),
                  placement: 'bottomRight'
                })
              })
              .finally(() => {
                if (isSubscribed.current) {
                  setIsLoading(false)
                }
              })
          }}
          unCheckedChildren={t('common.inactive')}
        />
      ),
      title: (
        <Typography.Text strong>{t('common.status.title')}</Typography.Text>
      )
    },
    {
      dataIndex: '',
      render: (record: PolicyAntiFraud) => (
        <Button
          data-test-id="antifraud-edit"
          icon="edit"
          onClick={() => {
            refFormik.current?.resetForm(record)
          }}
          type="ghost"
        />
      ),
      width: 50
    }
  ]

  function handleSubmit(values: PolicyAntiFraud): void {
    const record = sanitizeToPost(JSON.parse(JSON.stringify(values)))
    setIsLoading(true)
    PoliciesService.saveAntiFraud({
      organization_id: me.user.organization_id,
      record
    })
      .then(response => {
        if (isSubscribed.current && response.status === 200) {
          const newRecord: PolicyAntiFraud & { key: Id } = {
            ...response.data.policy_request_receipt_antifrauds[0],
            key: response.data.policy_request_receipt_antifrauds[0]
              .policy_request_receipt_antifraud_id
          }

          if (typeof values.policy_request_receipt_antifraud_id === 'string') {
            // is new
            setDataSource(data => [...data, newRecord])
          } else {
            // update
            setDataSource(data =>
              data.map(d =>
                d.policy_request_receipt_antifraud_id ===
                newRecord.policy_request_receipt_antifraud_id
                  ? newRecord
                  : d
              )
            )
          }
          refFormik.current.resetForm(initialValues)
          notification.success({
            duration: 10,
            message: t('common.edit-done-successfully'),
            placement: 'bottomRight'
          })
          return
        }
        notification.warning({
          duration: 10,
          message: t('common.response.unexpected-error'),
          placement: 'bottomRight'
        })
      })
      .catch(response => {
        notification.warning({
          duration: 10,
          message: t('common.response.unexpected-error'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        if (isSubscribed.current) {
          setIsLoading(false)
        }
      })
  }

  return (
    <>
      <CSS.Breadcrumbs>{header}</CSS.Breadcrumbs>
      <PageHeader
        extra={[
          <Button
            data-test-id="antifraud-add"
            key="common.actions.add"
            onClick={() => {
              refFormik.current.resetForm(
                getDefaultValues(me.user.organization_id)
              )
            }}
            size="default"
            title="common.actions.add"
            type="primary"
          />
        ]}
        title={title}
      />
      <CSS.Content>
        <Table
          className="table-striped"
          columns={columns}
          data-test-id="antifraud-list"
          dataSource={dataSource}
          loading={isLoading}
          locale={{
            emptyText: t('common.no-data')
          }}
          pagination={false}
        />
      </CSS.Content>
      <AntiFraudDrawer
        handleSubmit={handleSubmit}
        meLevelType={me.user.user_level_type}
        refFormik={refFormik}
      />
    </>
  )
}
